
import React from "react";
import NativeInterface from "../../services/NativeInterface"
import {GeolocationLayer, GeolocationProps} from "./GeolocationLayer"
import {MarkersLayer, MarkersProps} from "./MarkersLayer"
import {ControlsLayer, ControlsProps} from "./ControlsLayer"
import {ContentLayer, ContentProps} from "./ContentLayer"
import {MonitorLayer, MonitorProps} from "./MonitorLayer"
import { useLanguage } from "pages/dimension/LanguageContext";

const Map: React.FC<{
        mapId?: string
        onClickHandler?: (event: google.maps.MapMouseEvent)=>void
        geolocation?: GeolocationProps                  
        markers?: MarkersProps
        controls?: ControlsProps
        content?: ContentProps
        monitor?: MonitorProps }> = (props) => {
    const BIG_BEN_LOCATION = {lat: 51.50086954113261, lng: -0.12465339540453466};
    const divRef = React.useRef<HTMLDivElement>(null);
    const mapRef = React.useRef<google.maps.Map | null>(null);
    const markersBoundsRef = React.useRef<boolean | null>(null);

    const geolocationLayer = React.useRef<GeolocationLayer | null>(null);
    const markersLayer = React.useRef<MarkersLayer | null>(null);
    const controlsLayer = React.useRef<ControlsLayer | null>(null);
    const contentLayer = React.useRef<ContentLayer | null>(null);
    const monitorLayer = React.useRef<MonitorLayer | null>(null);
    const langCtx = useLanguage();

    function onMapClick(event: google.maps.MapMouseEvent) {
        console.log(`MapMouseEvent: ${JSON.stringify(event)}`);
        markersLayer.current?.onMapClick(event)
        contentLayer.current?.onMapClick(event)
        monitorLayer.current?.onMapClick(event)
        if (props.onClickHandler) {
            props.onClickHandler(event)
        }
    };

    function onMapIdle() {
        //console.log('onIdle');
    }

    React.useEffect(() => {
        return () => {
            deinitialize()
        }
    }, []);

    function deinitialize() {
        if (contentLayer.current) {
            contentLayer.current.deinitialize()
        }
    }
    
    React.useEffect(() => {
        console.log("EFFECT langCtx")
        contentLayer.current?.updateFilterTitle(langCtx)
    }, [langCtx]);

    React.useEffect(() => {
        if (divRef.current) {
            mapRef.current = createMap(divRef.current);
        }
    }, [divRef]);

    React.useEffect(() => {
        console.log("EFFECT props.geolocation")
        geolocationLayer.current?.configure(props.geolocation)
    }, [props.geolocation])

    React.useEffect(() => {
        console.log("EFFECT props.markers")
        markersLayer.current?.configure(props.markers)

        if (mapRef.current) {
            tryFitContentBounds(mapRef.current)
        }
    }, [props.markers]);

    React.useEffect(() => {
        console.log("EFFECT props.controls")
        controlsLayer.current?.configure(props.controls)
    }, [props.controls])

    React.useEffect(() => {
        console.log("EFFECT props.content")
        contentLayer.current?.configure(props.content)
        if (mapRef.current) {
            tryFitContentBounds(mapRef.current)
        }
    }, [props.content])

    React.useEffect(() => {
        console.log("EFFECT props.monitor")
        monitorLayer.current?.configure(props.monitor)
        if (mapRef.current) {
            tryFitContentBounds(mapRef.current)
        }
    }, [props.monitor])

    function createMap(div: HTMLDivElement) {
/*
        let styles: google.maps.MapTypeStyle[] = [
            {
                featureType: "poi",
                stylers: [{ visibility: "off" }]
            },
            {
                featureType: "transit",
                elementType: "labels.icon",
                stylers: [{ visibility: "off" }],
            }
        ]
*/
        const styles = [
            {
                elementType: "labels.icon",
                stylers: [{ visibility: "off" }],
            }
        ];

        let options: google.maps.MapOptions = {
            //mapId: props.mapId, // Google styles don't seem to work. Setting styles explicitly here for now.
            styles: styles, // map style is managed in google console
            disableDefaultUI: true,
            zoomControl: !NativeInterface.isPWA(),
            clickableIcons: false,
            keyboardShortcuts: false
//            idle: onMapIdle
        }

        let map = new window.google.maps.Map(div, options)
        map.addListener("click", onMapClick);
        map.addListener("idle", onMapIdle);

        geolocationLayer.current = new GeolocationLayer(map, props.geolocation)
        markersLayer.current = new MarkersLayer(map, props.markers)
        controlsLayer.current = new ControlsLayer(map, {...props.controls, ...{recenterCallback: recenter}})

        if (contentLayer.current) {
            contentLayer.current.deinitialize()
        }
        contentLayer.current = new ContentLayer(langCtx, map, props.content)
        monitorLayer.current = new MonitorLayer(map, props.monitor)

        tryResetBounds(map)

        return map
    }

    function tryResetBounds(map: google.maps.Map) {
        let location = geolocationLayer.current?.location()
        if (location) {
            map.setCenter({ lat: location.latitude, lng: location.longitude });
            map.setZoom(16);
            markersBoundsRef.current = true
            return;
        }
        if (tryFitContentBounds(map)) {
            return
        }


        map.setCenter(BIG_BEN_LOCATION);
        map.setZoom(13);
    }

    function tryFitContentBounds(map: google.maps.Map) {
        if (markersBoundsRef.current === true) {
            return false
        }

        let bounds = markersLayer.current?.getBounds()
        let contentBounds = contentLayer.current?.getBounds()
        if (contentBounds) {
            if (bounds) {
                bounds.extend(contentBounds.getNorthEast())
                bounds.extend(contentBounds.getSouthWest())
            } else {
                bounds = contentBounds
            }
        }

        let monitorBounds = monitorLayer.current?.getBounds()
        if (monitorBounds) {
            if (bounds) {
                bounds.extend(monitorBounds.getNorthEast())
                bounds.extend(monitorBounds.getSouthWest())
            } else {
                bounds = monitorBounds
            }
        }

        if (bounds) {
            markersBoundsRef.current = true
            map.fitBounds(bounds)
            return true
        }

        return false
    }

    function recenter() {
        let location = geolocationLayer.current?.location()
        if (location) {
            mapRef.current?.setCenter({ lat: location.latitude, lng: location.longitude });
            mapRef.current?.setZoom(16);
          };
    }

    return (
        <div ref={divRef} style={{width: "100%", height: "100%"}}/>
    );
};

export default Map;